import { StyledRange } from "@silevis/reactgrid";
import { getIn } from "final-form";
import moment from "moment";
import {
  ApplicationContextType,
  UnderwritingSteps,
  uploadStates,
} from "../../../interfaces/interfaces";
import { findFirstDebtScheduleGridError } from "./DebtSchedule/debtScheduleHelper";
import { findFirstDebtorScheduleGridError } from "./DebtorSchedule/debtorScheduleHelper";

export enum metricsName {
  grossSales = "Gross Sales",
  transactionDetails = "Transaction Details",
  productCosts = "Product Costs",
  logisticAndPackagingCosts = "Logistic and Packaging Costs",
  marketingExpenses = "Marketing Expenses",
  additionalCosts = "Additional Costs",
  operatingExpenses = "Operating Expenses",
  loanServicing = "Loan Servicing",
}

export const dividerMetrics = new Set([
  "Gross Sales",
  "Transaction Details",
  "Product Costs",
  "Logistic and Packaging Costs",
  "Marketing Expenses",
  "Additional Cost",
  "Operating Expenses",
  "Loan Servicing",
]);

export const generateProvisionalStatement = (
  startDate: string,
  endDate: string
) => {
  const endMonth = moment(endDate, "DD-MM-YYYY");
  const startMonth = moment(startDate, "DD-MM-YYYY");
  const differenceInMonths = endMonth.diff(startMonth, "months");
  const months = [startMonth.clone().format("MM-YYYY")];
  for (let i = 1; i <= differenceInMonths; i++) {
    months.push(startMonth.clone().add(i, "months").format("MM-YYYY"));
  }
  //   console.log("differenceInMonths", differenceInMonths);
  const headers = ["INR in Lakhs", ...months.reverse()];
  const metrics = [
    "Gross Sales",
    "D2C",
    "Amazon",
    "Myntra",
    "Offline",
    "Others",
    "Transaction Details",
    "Returns",
    "Refunds",
    "Collected Taxes",
    "Collected Shipping",
    "COD",
    "Others",
    "Product Costs",
    "Cost of Goods Sold",
    "Logistics and Packaging Costs",
    "Shipping Cost",
    "Packaging",
    "Marketing Expenses",
    "Meta",
    "Google",
    "Amazon",
    "Others",
    "Additional Cost",
    "Platform Commission",
    "Branding",
    "Agency Fees",
    "Operating Expenses",
    "Rent",
    "Salaries",
    "Loan Servicing",
    "Loan 1",
    "Loan 2",
    "Loan 3",
    "Loan 4",
  ];
  const rowData = metrics.map((metric) => {
    const monthEmptyArray = Array.from({ length: months.length }, () => "");
    return [metric, ...monthEmptyArray];
  });
  return {
    headers,
    rowData,
  };
};

export const getGridInvalidRange = (gridData: any) => {
  const newStyledRanges: StyledRange[] = [];

  gridData.rowData.forEach((row, rowIndex) => {
    if (dividerMetrics.has(row[0])) return; // Skip dividers

    row.forEach((cell, colIndex) => {
      if (cell.toString().trim() === "") {
        // If cell is empty
        newStyledRanges.push({
          range: {
            start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
            end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
          },
          styles: { background: "#FDF1F1" }, // Light red
        });
      }
    });
  });
  return newStyledRanges;
};

export const checkIfGridDataIsValid = (gridData: any) => {
  const isInvalid = gridData.rowData.some((row, rowIndex) => {
    if (dividerMetrics.has(row[0])) return; // Skip dividers
    return row.some((cell) => cell.toString().trim() === "");
  });
  return isInvalid;
};

export const revenueFormValidation = (values, underwritingFileJobs) => {
  const errors = {};
  const allRevenueJobs = getIn(
    underwritingFileJobs,
    `${UnderwritingSteps.revenue}`
  );
  const missingData = [];
  const missingDocs = [];
  const requirePassword = [];
  if (values?.revenueDetails?.length) {
    const revenueDetailsError = [];
    values.revenueDetails.forEach((revenueDetail, outerIndex) => {
      let revenueErrorObj = {};
      const financialYearDetails = revenueDetail?.financialYearDetails;
      if (revenueDetail?.revenue || revenueDetail?.revenue?.toString() == "0") {
        if (financialYearDetails?.isAuditedFinancialRequired) {
          if (!revenueDetail?.auditedFinancials?.length) {
            // revenueErrorObj["auditedFinancials"] = "Upload Audited Financials";
            // missingDocs.push("Upload Audited Financials");
          } else {
            const passwordRequired = revenueDetail?.auditedFinancials?.some(
              (auditedFinancial, index) => {
                const uploadedFileJob =
                  allRevenueJobs[
                    `revenueDetails[${outerIndex}].auditedFinancials[${index}]`
                  ];
                // console.log("uploadedFileJob", uploadedFileJob);
                if (uploadedFileJob?.status == uploadStates?.requiredPassword) {
                  return true;
                }
              }
            );
            if (passwordRequired) {
              revenueErrorObj["auditedFinancials"] = "Password missing";
              requirePassword.push("Password missing");
            }
          }
        } else {
          if (!revenueDetail?.isAuditedFinancials) {
            if (!revenueDetail?.provisionalStatement) {
              revenueErrorObj["provisionalStatement"] = "Details Missing";
              missingData.push("Details Missing");
            } else {
              const invalidRange = checkIfGridDataIsValid(
                revenueDetail?.provisionalStatement
              );
              if (invalidRange) {
                revenueErrorObj["provisionalStatement"] = "Details Missing";
                missingData.push("Details Missing");
              }
            }
          }
        }
      } else {
        if (revenueDetail?.auditedFinancials?.length || outerIndex == 0) {
          revenueErrorObj["revenue"] = "Enter Revenue";
          missingData.push("Enter Revenue");
        }
      }
      revenueDetailsError.push(revenueErrorObj);
    });
    if (revenueDetailsError.length) {
      errors["revenueDetails"] = revenueDetailsError;
    }
  }

  if (!values?.hasDebt) {
    errors["hasDebt"] = "Select an option";
    missingData.push("Select an option");
  }

  if (values?.hasDebt == "yes") {
    if (!values?.debtSchedule) {
      errors["debtSchedule"] = "Details Missing";
      missingData.push("Details Missing");
    } else {
      const invalidRange = findFirstDebtScheduleGridError(values?.debtSchedule);
      // console.log("invalidRange", invalidRange);
      if (invalidRange) {
        errors["debtSchedule"] = "Details Missing";
        missingData.push("Details Missing");
      }
    }
  }

  // if (!values?.hasReceivables) {
  //   errors["hasReceivables"] = "Select an option";
  //   missingData.push("Select an option");
  // }
  // if (values?.hasReceivables == "yes") {
  //   if (!values?.debtorSchedule) {
  //     errors["debtorSchedule"] = "Details Missing";
  //     missingData.push("Details Missing");
  //   } else {
  //     const invalidRange = findFirstDebtorScheduleGridError(
  //       values?.debtorSchedule,
  //       ApplicationContextType.underwriting
  //     );
  //     if (invalidRange) {
  //       errors["debtorSchedule"] = "Details Missing";
  //       missingData.push("Details Missing");
  //     }
  //   }
  // }

  // console.log("values", values);

  return {
    errors,
    missingData,
    missingDocs,
    requirePassword,
  };
};
